import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
  usePropertyAddressUrl,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy alongside it for use on
// light primary backgrounds. Source SVG mirrors `repli_white.svg` with the
// wordmark's `fill:#fff` swapped to `#1a1a1a` (matches the dark color used
// by the tokens generator's `getContrastTextColor`).
import repliBlackLogoUrl from '../assets/repli_black.svg';

// ============================================================================
// Contrast text color
// ----------------------------------------------------------------------------
// Mirrors `getContrastTextColor` from `@vibesites/tokens` (defined in
// `vendor/vibesites/tokens/generator.ts`, currently module-private and not
// re-exported, so we replicate the exact logic here verbatim — including the
// `0.55` luminance threshold and the `'#ffffff'` / `'#1a1a1a'` return values).
// Using the same function keeps logo color choice (white vs black) in lockstep
// with the auto-computed `--vibe-color-text-on-primary` CSS variable, so the
// footer's text and logos always pick the same contrast side.
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
const TEXT_ON_PRIMARY_DARK = '#1a1a1a';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns the contrasting text color (`'#ffffff'` or `'#1a1a1a'`) for a given
 * background hex. Identical to `getContrastTextColor` in `@vibesites/tokens`.
 * Falls back to the white-on-dark side when the input can't be parsed, which
 * matches how the tokens generator treats unknown / missing primary colors.
 */
function getContrastTextColor(bgHex: string | null | undefined): string {
  if (!bgHex) return TEXT_ON_PRIMARY_LIGHT;
  const luminance = getRelativeLuminance(bgHex);
  // Favor white text on medium tones (gold, tan, dusty blue) where dark text is
  // technically higher-contrast but reads as heavy/muddy. Flip to dark only on
  // genuinely light backgrounds (pastels, cream, near-white).
  return luminance > 0.55 ? TEXT_ON_PRIMARY_DARK : TEXT_ON_PRIMARY_LIGHT;
}

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Sort by canonical day order
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];

  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;

    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || entry.day;
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry.day || '').slice(0, 3);
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the responsive renderer can swap them at the tablet
      // breakpoint without re-deriving the grouping.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place/business/address
    // fields). An explicit override from the editor still takes precedence.
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};


// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');

  // ---- Contrast-driven logo variant selection ----
  // Decide whether text-on-primary should be white or dark using the same
  // `getContrastTextColor` logic the tokens generator uses to compute
  // `--vibe-color-text-on-primary`. We then pick mono_white / mono_black logo
  // defaults (and the Multihub + Repli marks) to match that decision, so the
  // footer logos always sit on the correct contrast side regardless of how
  // dark/light the property's primary brand color is. The runtime-adjusted
  // slot definitions are passed to both `useSlotGroup` and `EditableSlotGroup`
  // so the editor surfaces the same default the renderer is using.
  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const runtimeLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: {
            ...logoSlotDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  const runtimeManagedByLogoDef: SlotGroupDefinition = useWhiteOnPrimary
    ? managedByLogoDef
    : {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: {
            ...managedByLogoDef.fields.image,
            defaultField: 'property_company_mono_black_logo',
          },
        },
      };

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', runtimeManagedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe IQ widget is injected at runtime by an external script
  // and sets its own inline `style="z-index: …"` on the launcher after mount,
  // which beats anything a stylesheet declares. The CSS rule further down in
  // the render covers the known launcher selectors as a cheap first pass; this
  // effect is the authoritative fix.
  //
  // Approach: watch document.body for added/changed nodes that match the real
  // launcher only (`.rcc-app-cta-wrap`, `.rcc-app-controls`,
  // `#conversations-webchat-toggle`). Pin those to 120 with `!important` —
  // just above the open header chrome — so the vendor inline style still
  // loses. Only also pin an outermost `position: fixed` ancestor when that
  // ancestor is the chat wrap itself; never lift unrelated fixed shells.
  //
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point without having to
  // introduce a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const LAUNCHER_SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const CHAT_WRAP_SELECTOR = '.rcc-app-cta-wrap';
    const Z = '120';

    function isLauncher(el: Element): boolean {
      return el.matches(LAUNCHER_SELECTOR);
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!isLauncher(el)) return;
      pin(el);
      // Only pin a fixed ancestor when it is the chat wrap — never an
      // unrelated outermost fixed shell the launcher happens to sit in.
      const fixedAncestor = outermostFixedAncestor(el);
      if (
        fixedAncestor &&
        fixedAncestor !== el &&
        fixedAncestor.matches(CHAT_WRAP_SELECTOR)
      ) {
        pin(fixedAncestor);
      }
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(LAUNCHER_SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      // The root node itself can also be a match (querySelectorAll skips it).
      if (root instanceof Element && isLauncher(root)) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only react to attribute changes that could affect identification or
      // stacking — anything else (e.g. inline transforms during animation) is
      // noise we want to ignore.
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      className="text-primary-foreground"
      style={{ backgroundColor: 'var(--vibe-color-primary)' }}
    >

      {/* ---- Main Footer Content ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6 md:pt-10 md:pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">

          {/* Column 1: Logo */}
          <div className="flex flex-col items-center">
            <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-20 w-auto max-w-[190px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-primary-foreground">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Column 2: Contact Info */}
          <div className="flex flex-col items-center gap-5">
            {/* Phone */}
            <div className="text-center">
              <RichTextContent path="footer.call_us_label" defaultValue="<p>Call Us</p>" as="p" className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground mb-1" />
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref font-body text-[13px] text-primary-foreground hover:text-[var(--vibe-color-text-on-primary)]/70 transition-colors"
                style={{ fontFamily: 'var(--vibe-font-body)' }}
              >
                <EditableToken
                  path="footer.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            {/* Address */}
            <div className="text-center">
              <RichTextContent path="footer.visit_us_label" defaultValue="<p>Visit Us</p>" as="p" className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground mb-1" />
              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-[13px] text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors block"
                >
                  {addressLink.text || '123 Main Street, City, ST 00000'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Column 3: Office Hours */}
          <div className="flex flex-col items-center">
            <RichTextContent path="footer.office_hours_label" defaultValue="<p>Office Hours</p>" as="p" className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground mb-3" />
            {groupedHours.length > 0 ? (
              <dl className="space-y-2 w-full max-w-[280px]">
                {groupedHours.map((group, index) => (
                  <div key={index} className="flex justify-between font-body text-sm text-[var(--vibe-color-text-on-primary)]/80">
                    <dt>
                      {/* Full names on mobile and desktop; abbreviated on tablet
                          (md → lg) where the 3-column grid leaves the hours
                          column too narrow for "Monday - Friday". */}
                      <span className="md:hidden lg:inline">{group.label}</span>
                      <span className="hidden md:inline lg:hidden">{group.shortLabel}</span>
                    </dt>
                    <dd className="text-right ml-4">{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-body text-sm text-[var(--vibe-color-text-on-primary)]/40">Office hours will be displayed here.</p>
            )}
          </div>

        </div>
      </div>

      {/* ---- HR Divider ---- */}
      {/* Picks black-on-light vs white-on-dark using the same `useWhiteOnPrimary`
          luminance branch as the logos, so the divider stays visible on both
          light and dark primary colors. We can't use Tailwind's opacity
          modifier on `var(--vibe-color-text-on-primary)` directly because the
          var resolves to a hex string, not the RGB-channel triplet Tailwind's
          `/20` syntax expects — so the slash opacity silently no-ops and the
          border falls back to the default (white). */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <hr className={`border-t ${useWhiteOnPrimary ? 'border-white/20' : 'border-[#1a1a1a]/20'}`} />
      </div>

      {/* ---- Compliance Icons + Legal Nav ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex flex-col items-center gap-4">
          {/* Compliance icons in a row */}
          <div className="flex items-center justify-center gap-5 text-[var(--vibe-color-text-on-primary)]/60">
            <WheelchairIcon size={28} color="currentColor" />
            <FairHousingIcon size={28} color="currentColor" />
            <PetsAllowedIcon size={28} color="currentColor" />
          </div>

          {/* Legal nav links with dot separators */}
          <nav className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1 font-body text-[13px] text-primary-foreground" aria-label="Legal links">
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                {accessibilityLink.text || 'Accessibility'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">·</span>
            <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
              <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                {privacyPolicyLink.text || 'Privacy Policy'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">·</span>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                {sitemapLink.text || 'Sitemap'}
              </a>
            </EditableSlotGroup>
          </nav>
        </div>
      </div>

      {/* ---- Bottom Bar: Responsive Stacked / Two-Zone Credit Row ---- */}
      {/* Single flat flex container with CSS `order` rewiring + per-breakpoint
          width tricks to drive two very different layouts from one render path:

          Mobile (default):
            Row 1: Managed By (centered, full width via `w-full`)
            Row 2: Powered by (Multihub) + Website Design by (Repli) — paired,
                   centered by parent `justify-center`
            Row 3: Copyright (centered, full width via `w-full`)
            Labels are visible on every breakpoint now (per user request);
            Managed By and Copyright each get `w-full` so they grab a whole
            flex-wrap row, leaving Multihub+Repli to share row 2 together.

          Desktop (md+):
            Single row: [Managed By] [Copyright] →→ [Multihub] [Repli]
            Managed By keeps `order-1`; Copyright is moved to `md:order-2` and
            given `md:w-auto md:mr-auto` — the auto right-margin consumes all
            leftover space, pushing the Multihub/Repli pair (orders 3 & 4) to
            the right edge.

          Each `<EditableSlotGroup>` is itself the flex item (we apply order +
          mobile centering via its `className` prop) so we don't introduce
          wrapper divs that would interfere with the editor's hover/click
          affordances on the slot. For the Managed By group, `w-full flex
          justify-center` on mobile makes the wrapper a centering flex
          container so the inner `<a>` (which has its own `flex items-center
          gap-2`) is centered horizontally; `md:w-auto md:justify-start` reverts
          to natural content-width left-aligned on desktop. */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6 mt-4">
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-3 font-body text-[10px] text-[var(--vibe-color-text-on-primary)]/60">

          {/* Managed By — mobile: row 1 alone (w-full + center), desktop: order-1 left zone */}
          <EditableSlotGroup
            groupPath="footer.managed_by_logo"
            definition={runtimeManagedByLogoDef}
            className="order-1 w-full flex justify-center md:w-auto md:justify-start"
          >
            {managedByLogo.image ? (
              <a
                href={managedByLogo.url || '#'}
                target={managedByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.managed_by_text"
                  defaultValue="<p>Professionally Managed by</p>"
                  as="span"
                  className="text-primary-foreground"
                />
                <VibesitesImage
                  url={managedByLogo.image}
                  alt={managedByLogo.alt || 'Property Management Company'}
                  className="h-4 w-auto max-w-[110px] object-contain"
                />
              </a>
            ) : null}
          </EditableSlotGroup>

          {/* Powered By (Multihub) — mobile: row 2 left half (paired with Repli),
              desktop: order-3 right zone */}
          <EditableSlotGroup
            groupPath="footer.powered_by_logo"
            definition={poweredByLogoDef}
            className="order-2 md:order-3"
          >
            <a
              href={poweredByLogo.url || 'https://www.multihub.io/'}
              target={poweredByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
            >
              <RichTextContent
                path="footer.powered_by_text"
                defaultValue="<p>Powered by</p>"
                as="span"
                className="text-primary-foreground"
              />
              {useWhiteOnPrimary ? (
                <MultihubLogoMonoWhite height={14} alt={poweredByLogo.alt || 'Multihub'} />
              ) : (
                <MultihubLogoMonoBlack height={14} alt={poweredByLogo.alt || 'Multihub'} />
              )}
            </a>
          </EditableSlotGroup>

          {/* Website Design By (Repli) — mobile: row 2 right half (paired with Multihub),
              desktop: order-4 right zone */}
          <EditableSlotGroup
            groupPath="footer.design_by_logo"
            definition={designByLogoDef}
            className="order-3 md:order-4"
          >
            <a
              href={designByLogo.url || 'https://www.repli360.com/'}
              target={designByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
            >
              <RichTextContent
                path="footer.design_by_text"
                defaultValue="<p>Website Design by</p>"
                as="span"
                className="text-primary-foreground"
              />
              <VibesitesImage
                url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                alt={designByLogo.alt || 'Repli Logo'}
                height={16}
                className="h-4 w-auto"
                priority
              />
            </a>
          </EditableSlotGroup>

          {/* Copyright (year auto-updates from the runtime clock so the footer
              never goes stale and no token edit is needed for the year rollover).
              - Mobile: order-4 + `w-full` forces a flex-wrap break onto its own
                final row, centered.
              - Desktop: order-2 puts it between Managed By and Powered By, and
                `md:mr-auto` consumes the leftover space so the Multihub/Repli
                pair gets pushed to the right edge. */}
          <span className="order-4 w-full text-center md:order-2 md:w-auto md:text-left md:mr-auto">
            © {new Date().getFullYear()} All Rights Reserved.
          </span>

        </div>
      </div>

      {/* ---- Global z-index fallback for the RentCafe IQ chatbot ----
          Cheap first pass for the known launcher selectors. The authoritative
          fix lives in the `useEffect` above — MutationObserver + inline
          `!important` so the vendor's post-mount inline z-index still loses.
          Kept at 120, just above the open header chrome. */}
      <style>{`
        /* Production RentCafe Connect / IQ launcher only:
           floating wrap .rcc-app-cta-wrap, controls .rcc-app-controls,
           toggle #conversations-webchat-toggle. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.call_us_label': {
      label: 'Call Us Label',
      type: 'richtext',
      defaultValue: '<p>Call Us</p>',
    },
    'footer.visit_us_label': {
      label: 'Visit Us Label',
      type: 'richtext',
      defaultValue: '<p>Visit Us</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
